import type { SledTeam } from '@sdd-maturity/shared-types';
import { useLocation } from 'react-router-dom';
import { useScopeNode } from './useScopeNode';
import {
  type ScopeLens,
  type BreadcrumbSegment,
  ROOT_NODE,
  parseScopePath,
  parseLeafEntity,
  buildBreadcrumbPath,
  lensFromPath,
  tierForSegment,
} from '../lib/hierarchy-route';

export interface ActiveScope {
  /** The node that owns the page-header tabs, or null when there is none. */
  node: SledTeam | null;
  /** The lens (tab) the current URL represents — always one of the three tabs. */
  lens: ScopeLens;
  /** Whether to render the tab strip (a node exists and the route is tabbable). */
  showTabs: boolean;
  /**
   * FR-3: the breadcrumb trail — the node's scope chain (root → node), plus the leaf
   * entity as a terminal segment on a feature/repo leaf. Empty when there is no node.
   */
  path: BreadcrumbSegment[];
}

/** Calibration owns no node-scoped tabs; everything else with a node is tabbable
 *  (scanning lives inline at the bottom of the Discovery/Delivery pages). */
function isTabbableRoute(pathname: string): boolean {
  return !pathname.startsWith('/calibration');
}

/**
 * FR-2: resolve the node-scoped page header's `{ node, lens, showTabs }` purely from
 * the URL against the cached `getAllTeams()` tree. The bare lens routes (`/`,
 * `/discovery`, `/delivery`) are the synthetic "Cox Automotive" root scope; typed
 * scope routes — including the canonical FR-5 leaves — carry the node `number` and
 * the lens in the path. The lens comes straight from the path so exactly one tab is
 * always lit within a scope (AC-2.4), never the all-dark `NavLink` state.
 */
export function useActiveScope(): ActiveScope {
  const { pathname } = useLocation();

  const parsed = parseScopePath(pathname);
  const urlNumber = parsed.kind === 'typed' ? parsed.number : undefined;
  const expectedTier = parsed.kind === 'typed' ? tierForSegment(parsed.segment) : undefined;

  const { node: urlNode, teams } = useScopeNode(urlNumber, expectedTier);

  const isRoot = parsed.kind === 'root';
  const node = isRoot ? ROOT_NODE : urlNode;
  const lens: ScopeLens = isRoot
    ? parsed.lens
    : lensFromPath(pathname, parsed.kind === 'typed' ? parsed.lensParam : undefined);
  const showTabs = node !== null && isTabbableRoute(pathname);

  // FR-3: scope chain (root → node) plus the leaf entity when on a feature/repo leaf.
  // The root scope has an empty trail — the breadcrumb's hard-coded "Cox Automotive"
  // anchor is its only crumb.
  const scopeChain = node && !isRoot ? buildBreadcrumbPath(node, teams) : [];
  const leaf = parseLeafEntity(pathname);
  const path = node && !isRoot && leaf ? [...scopeChain, leaf] : scopeChain;

  return { node, lens, showTabs, path };
}
